import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import SearchableSelect from "./SearchableSelect";

const AssignLoansModal = ({ isOpen, onClose }) => {
  const buckets = [
    { label: "1-7 Days", value: { min: 1, max: 7 } },
    { label: "8-14 Days", value: { min: 8, max: 14 } },
    { label: "15-21 Days", value: { min: 15, max: 21 } },
    { label: "22-30 Days", value: { min: 22, max: 30 } },
    { label: "30+ Days", value: { min: 30, max: 100 } },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLoans, setSelectedLoans] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [selectedOfficers, setSelectedOfficers] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loans, setLoans] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const loansPerPage = 10;

  const totalPages = Math.ceil(totalCount / loansPerPage);

  // Fetch officers
  const fetchOfficers = async () => {
    setLoadingOfficers(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_HOST_COLLECTIONS}/collections-officers`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${localStorage.accessToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      const officersList = data.collectionsOfficers || [];
      // Sort officers by first name alphabetically
      const sortedOfficers = officersList.sort((a, b) => {
        const firstNameA = (a.firstName || "").toLowerCase();
        const firstNameB = (b.firstName || "").toLowerCase();
        return firstNameA.localeCompare(firstNameB);
      });
      setOfficers(sortedOfficers);
    } catch (error) {
      console.error("Error fetching officers:", error);
    } finally {
      setLoadingOfficers(false);
    }
  };

  // Format currency helper
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number helper
  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Fetch loans for current page
  const fetchLoans = async () => {
    setLoading(true);
    try {
      const bucket = selectedBucket
        ? JSON.stringify(buckets.find(b => b.value.min.toString() === selectedBucket)?.value || "")
        : "";
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
      const minAmountParam = minAmount ? `&minAmount=${encodeURIComponent(minAmount)}` : "";
      const maxAmountParam = maxAmount ? `&maxAmount=${encodeURIComponent(maxAmount)}` : "";
      const response = await fetch(`${process.env.REACT_APP_API_HOST_COLLECTIONS}/unassigned-loans?page=${currentPage}&limit=${loansPerPage}&bucket=${bucket}${searchParam}${minAmountParam}${maxAmountParam}`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${localStorage.accessToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      setLoans(data.data);  // Only the current page's loans
      setTotalCount(data.pagination.totalCount);
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search handler
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOpen) {
        fetchLoans();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen) {
      fetchOfficers();
      fetchLoans();
    }
  }, [isOpen, currentPage, selectedBucket, minAmount, maxAmount]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedLoans([]);
      setIsAllSelected(false);
      setSelectAllMode(false);
      setSelectedOfficers([]);
      setSelectedBucket("");
      setMinAmount("");
      setMaxAmount("");
      setSearchTerm("");
      setCurrentPage(1);
      setAutoAssignEnabled(false);
    }
  }, [isOpen]);

  // Page-level selection (default behavior)
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedLoans([]);
      setIsAllSelected(false);
    } else {
      const allLoanIds = loans.map((loan) => loan._id);
      setSelectedLoans(allLoanIds);
      setIsAllSelected(true);
    }
  };

  // Opt-in bulk selection for all matching loans
  const handleSelectAllMatching = async () => {
    if (selectAllMode) {
      // Clear selection
      setSelectedLoans([]);
      setIsAllSelected(false);
      setSelectAllMode(false);
    } else {
      // Enable "select all matching" mode
      setSelectAllMode(true);
      setIsAllSelected(true);

      // Fetch first 1000 IDs for UI feedback
      setLoading(true);
      try {
        const bucket = selectedBucket
          ? JSON.stringify(buckets.find(b => b.value.min.toString() === selectedBucket)?.value || "")
          : "";
        const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
        const minAmountParam = minAmount ? `&minAmount=${minAmount}` : "";
        const maxAmountParam = maxAmount ? `&maxAmount=${maxAmount}` : "";

        const response = await fetch(
          `${process.env.REACT_APP_API_HOST_COLLECTIONS}/unassigned-loans?limit=1000&bucket=${bucket}${searchParam}${minAmountParam}${maxAmountParam}`,
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${localStorage.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setSelectedLoans(data.data.map(loan => loan._id));
      } catch (error) {
        console.error("Error fetching loan IDs:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLoanSelection = (loanId) => {
    setSelectedLoans((prev) =>
      prev.includes(loanId)
        ? prev.filter((id) => id !== loanId)
        : [...prev, loanId]
    );
  };

  const handleAssign = async () => {
    if (selectedOfficers.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Officer Selected",
        text: "Please select at least one officer to assign loans.",
      });
      return;
    }

    if (selectedLoans.length === 0 && !selectAllMode) {
      Swal.fire({
        icon: "warning",
        title: "No Loans Selected",
        text: "Please select at least one loan to assign.",
      });
      return;
    }

    setIsAssigning(true);
    try {
      const requestBody = selectAllMode
        ? {
            officerIds: selectedOfficers,
            filters: {
              bucket: selectedBucket
                ? buckets.find(b => b.value.min.toString() === selectedBucket)?.value
                : null,
              minAmount: minAmount || null,
              maxAmount: maxAmount || null,
              search: searchTerm || null,
            }
          }
        : {
            officerIds: selectedOfficers,
            loanIds: selectedLoans,
          };

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/assign-loans`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (response.ok) {
        // Build success message
        let message = result.message || `Successfully assigned ${result.totalProcessed} loans`;

        if (result.assignments && result.assignments.length > 1) {
          message += ':\n' + result.assignments.map(a =>
            `• ${a.loansAssigned} to ${a.officerName}`
          ).join('\n');
        }

        await Swal.fire({
          icon: "success",
          title: "Success",
          html: message.replace(/\n/g, '<br>'),
        });
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Assignment Failed",
          text: result.message || "Failed to assign loans. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error assigning loans:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while assigning loans. Please try again.",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[95%] max-w-7xl h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">New Assignment</h2>

          {/* Auto-Assign Toggle */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Auto-Assign Incoming Loans</span>
            <button
              onClick={() => setAutoAssignEnabled(!autoAssignEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoAssignEnabled ? "bg-blue-500" : "bg-gray-300"
              }`}
              title={autoAssignEnabled ? "Auto-assign is enabled" : "Auto-assign is disabled"}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoAssignEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {/* Modal Content */}
        <div className="flex-grow overflow-y-auto p-6 grid grid-cols-12 gap-6">
          {/* Filters Section */}
          <div className="col-span-2 border-r pr-6">
            <h3 className="font-bold text-lg mb-4 text-gray-700">Filters</h3>
            
            <div className="mb-4">
              <SearchableSelect
                label="Select a Bucket"
                options={[
                  { value: "", label: "All" },
                  ...buckets.map((bucket) => ({
                    value: bucket.value.min.toString(),
                    label: bucket.label,
                    bucket: bucket,
                  })),
                ]}
                value={selectedBucket}
                onChange={(value) => {
                  setSelectedBucket(value);
                  setCurrentPage(1);
                }}
                placeholder="All"
                searchable={true}
              />
            </div>

            <div className="mb-4">
              <label className="block font-bold mb-2 text-gray-700">Filter by Amount</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <input
                    type="number"
                    placeholder="Min Amount"
                    value={minAmount}
                    onChange={(e) => {
                      setMinAmount(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    placeholder="Max Amount"
                    value={maxAmount}
                    onChange={(e) => {
                      setMaxAmount(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              {(minAmount || maxAmount) && (
                <button
                  onClick={() => {
                    setMinAmount("");
                    setMaxAmount("");
                    setCurrentPage(1);
                  }}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Clear Amount Filter
                </button>
              )}
            </div>

            <div className="mb-4">
              <label className="block font-bold mb-2 text-gray-700">Search</label>
              <input
                type="text"
                placeholder="Search by loan number or client name..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full border rounded p-2 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Main Content Area */}
          <div className="col-span-10">
            {/* Officers Section */}
            <div className="mb-6">
              <h3 className="font-bold text-lg mb-4 text-gray-700">Assign To</h3>

              {loadingOfficers ? (
                <div className="text-gray-500 text-sm">Loading Officers...</div>
              ) : officers.length === 0 ? (
                <div className="text-red-500 text-sm">No officers found. Please ensure agents are assigned to you in the admin portal.</div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {officers.map((officer) => {
                      const displayName = officer.firstName;
                      const isSelected = selectedOfficers.includes(officer._id);

                      return (
                        <button
                          key={officer._id}
                          onClick={() => {
                            setSelectedOfficers(prev => {
                              if (prev.includes(officer._id)) {
                                return prev.filter(id => id !== officer._id);
                              } else {
                                return [...prev, officer._id];
                              }
                            });
                          }}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            isSelected
                              ? "bg-blue-500 text-white hover:bg-blue-600 ring-2 ring-blue-300"
                              : "bg-blue-100 text-gray-700 hover:bg-blue-200"
                          }`}
                        >
                          {displayName}
                          {isSelected && <span className="ml-2">✓</span>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selection Controls */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setSelectedOfficers(officers.map(o => o._id))}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Select All Officers
                    </button>
                    <button
                      onClick={() => setSelectedOfficers([])}
                      className="text-sm text-gray-600 hover:underline"
                    >
                      Clear Selection
                    </button>
                    {selectedOfficers.length > 0 && (
                      <span className="text-sm text-gray-600">
                        ({selectedOfficers.length} selected)
                      </span>
                    )}
                  </div>

                  {/* Distribution Preview */}
                  {selectedOfficers.length > 1 && (selectAllMode ? totalCount : selectedLoans.length) > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-2">
                        Distribution Preview
                      </h4>
                      <p className="text-sm text-blue-800 mb-3">
                        {selectAllMode ? totalCount : selectedLoans.length} loans will be divided equally among {selectedOfficers.length} officers:
                      </p>
                      <div className="space-y-1">
                        {(() => {
                          const loanCount = selectAllMode ? totalCount : selectedLoans.length;
                          const loansPerOfficer = Math.floor(loanCount / selectedOfficers.length);
                          const remainder = loanCount % selectedOfficers.length;

                          return selectedOfficers.map((officerId, index) => {
                            const officer = officers.find(o => o._id === officerId);
                            const loansForThisOfficer = loansPerOfficer + (index < remainder ? 1 : 0);

                            return (
                              <div key={officerId} className="flex justify-between text-sm">
                                <span className="text-gray-700">
                                  {officer?.firstName} {officer?.lastName}
                                </span>
                                <span className="font-medium text-blue-900">
                                  {loansForThisOfficer} loans
                                </span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="mb-4">
                <button
                  onClick={handleAssign}
                  className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  disabled={selectedLoans.length === 0 && !selectAllMode || selectedOfficers.length === 0 || isAssigning}
                >
                  {isAssigning
                    ? "Assigning..."
                    : `Assign ${selectAllMode ? totalCount.toLocaleString() : selectedLoans.length} ${selectAllMode ? "Loans" : selectedLoans.length === 1 ? "Loan" : "Loans"}${selectedOfficers.length > 1 ? ` to ${selectedOfficers.length} officers` : selectedOfficers.length === 1 ? ' to 1 officer' : ''}`
                  }
                </button>

                {selectedLoans.length > 0 && selectedOfficers.length === 0 && (
                  <p className="mt-2 text-sm text-red-600">
                    Please select at least one officer
                  </p>
                )}
              </div>
            </div>

            {/* Loans List Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="font-bold">{totalCount} matching loans</span>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 cursor-pointer"
                  checked={(isAllSelected && loans.length > 0) || selectAllMode}
                  onChange={handleSelectAll}
                  disabled={selectAllMode}
                  title={selectAllMode ? "All matching loans selected" : "Select all on this page"}
                />
                <label className="font-bold text-gray-700">
                  {selectAllMode ? `All ${totalCount.toLocaleString()} matching loans selected` : "Select/Unselect All Loans on Page"}
                </label>
              </div>

              {!selectAllMode && selectedLoans.length > 0 && totalCount > selectedLoans.length && (
                <button
                  onClick={handleSelectAllMatching}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Select all {totalCount.toLocaleString()} matching loans instead?
                </button>
              )}

              {selectAllMode && (
                <button
                  onClick={handleSelectAllMatching}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Deselect all matching loans
                </button>
              )}
            </div>

            {selectAllMode && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-blue-800 text-sm">
                  <strong>Selecting all {totalCount.toLocaleString()} matching loans</strong>
                  {" - "}The first 1,000 are shown here. All matching loans will be assigned.
                </p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <p className="mt-2 text-gray-600">Loading loans...</p>
              </div>
            ) : (
              <table className="min-w-full border-collapse border border-gray-200 rounded">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 cursor-pointer"
                        onChange={handleSelectAll}
                        checked={(isAllSelected && loans.length > 0) || selectAllMode}
                        disabled={selectAllMode}
                        title={selectAllMode ? "All matching loans selected" : "Select all on this page"}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Loan #</th>
                    <th className="px-6 py-3 text-left text-sm font-bold text-gray-700 border-b">Name</th>
                    <th className="px-6 py-3 text-center text-sm font-bold text-gray-700 border-b">Late Days</th>
                    <th className="px-6 py-3 text-right text-sm font-bold text-gray-700 border-b">Arrears</th>
                  </tr>
                </thead>
                <tbody>
                  {loans.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No loans found matching your criteria.
                      </td>
                    </tr>
                  ) : (
                    loans.map((loan) => (
                      <tr key={loan._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 border-b">
                        <input
                          type="checkbox"
                          checked={selectedLoans.includes(loan._id)}
                          onChange={() => handleLoanSelection(loan._id)}
                            className="cursor-pointer"
                        />
                      </td>
                        <td className="px-6 py-4 border-b text-blue-600 hover:text-blue-800">
                          <a href="/" className="hover:underline">{loan.loanNumber}</a>
                      </td>
                        <td className="px-6 py-4 border-b">{loan.client?.personal?.fullName || "N/A"}</td>
                        <td className="px-6 py-4 border-b text-center">{formatNumber(loan.lateDays)}</td>
                        <td className="px-6 py-4 border-b text-right font-medium">{formatCurrency(loan.due?.totalAllDue || loan.due?.totalDue || 0)}</td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <button
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
              >
                Previous
              </button>
                <span className="text-gray-700">Page {currentPage} of {totalPages}</span>
              <button
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
              >
                Next
              </button>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignLoansModal;
